import React from 'react'
import { Helmet } from 'react-helmet-async'
import { SITE, getPageForPath } from './pages'
import {
    buildSoftwareApplication,
    buildFAQ,
    buildBreadcrumb,
    buildHowTo,
    buildWebSite,
    buildOrganization,
    buildBlogPosting,
} from './schema'

export function SeoHead({ path, overrides = {} }) {
    // Safe guard — if path is missing, render nothing
    if (!path || typeof path !== 'string') {
        return null
    }

    const page = getPageForPath(path)

    if (!page) {
        const normalized = path === '/' || path.endsWith('/') ? path : `${path}/`
        const clean = normalized.split('?')[0].split('#')[0]
        return (
            <Helmet prioritizeSeoTags>
                <link rel="canonical" href={`${SITE.domain}${clean}`} />
                <meta name="robots" content="index, follow" />
            </Helmet>
        )
    }
    // ...

    const merged = { ...page, ...overrides }
    const url = `${SITE.domain}${merged.canonical}`

    const schemas = (merged.schema || [])
        .map((kind) => {
            switch (kind) {
                case 'SoftwareApplication': return buildSoftwareApplication(merged)
                case 'FAQPage': return buildFAQ(merged)
                case 'BreadcrumbList': return buildBreadcrumb(merged)
                case 'HowTo': return buildHowTo(merged)
                case 'WebSite': return buildWebSite()
                case 'Organization': return buildOrganization()
                case 'BlogPosting': return buildBlogPosting(merged)
                default: return null
            }
        })
        .filter(Boolean)

    return (
        <Helmet prioritizeSeoTags>
            <title>{merged.title}</title>
            <meta name="description" content={merged.metaDescription} />
            <link rel="canonical" href={url} />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:title" content={merged.title} />
            <meta property="og:description" content={merged.metaDescription} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={merged.ogImage || SITE.ogImage} />
            <meta property="og:locale" content="en_US" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={SITE.twitter} />
            <meta name="twitter:title" content={merged.title} />
            <meta name="twitter:description" content={merged.metaDescription} />
            <meta name="twitter:image" content={merged.ogImage || SITE.ogImage} />

            {schemas.map((schema, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    )
}

export default SeoHead